import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, ModalService, UserService } from '@cfp/core';
import {
  Alert,
  Button,
  FormGroupActionsDirective,
  FormGroupAfterLabelDirective,
  FormGroupComponent,
  InputPassword,
  InputText,
  Link,
  ModalContent,
  ValidationFeedback
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { LoginFormService } from '../../services';
import { ForgotPasswordModal } from '../forgot-password-modal';
import { LoginMessages } from '../login-messages';

@Component({
  selector: 'lib-login-modal',
  templateUrl: './login-modal.html',
  styleUrls: ['./login-modal.scss'],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    Link,
    FormGroupComponent,
    InputText,
    Button,
    InputPassword,
    FormGroupActionsDirective,
    LoginMessages,
    FormGroupAfterLabelDirective,
    ModalContent,
    Alert,
    ValidationFeedback
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginModal implements OnInit {
  public createAccount: EventEmitter<void> = new EventEmitter();
  public ssoLoginUrl?: string;
  public showLoginUsingSso: boolean = false;
  public forceSsoWhenEmailContains?: string;

  protected readonly isLoading = signal(false);
  protected readonly warningMessages = signal<string[]>([]);
  protected readonly errorMessages = signal<string[]>([]);
  protected readonly interpolateParams = signal<Record<string, string>>({});
  protected readonly shouldChangePassword = signal(false);
  protected readonly loginMessagesDataTest = signal<string | undefined>(undefined);
  protected readonly shouldLoginUsingSsoOnly = signal(false);

  protected form: FormGroup;

  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly loginFormService = inject(LoginFormService);

  public ngOnInit(): void {
    const group: any = {
      username: ['', Validators.required],
      password: ['', Validators.required]
    };

    this.form = this.formBuilder.group(group);

    const forceSsoWhenEmailContains = this.forceSsoWhenEmailContains;
    if (forceSsoWhenEmailContains) {
      this.form.controls['username'].valueChanges.subscribe((value: string) => {
        this.shouldLoginUsingSsoOnly.set(value?.indexOf(forceSsoWhenEmailContains) >= 0);
        if (this.shouldLoginUsingSsoOnly()) {
          document.getElementById('loginSso')?.focus();
        }
      });
    }

    setTimeout(() => {
      document.getElementById('username')?.focus();
    });
  }

  protected isFieldValid(field: string): boolean {
    return this.form.get(field)?.valid ?? false;
  }

  protected hide(): void {
    this.bsModalRef.hide();
  }

  protected createAccountClick(): void {
    this.createAccount.emit();
    this.hide();
  }

  protected showForgotPassword(): void {
    this.hide();
    setTimeout(() => {
      this.modalService.show(ForgotPasswordModal, {
        class: 'modal-sm',
        initialState: {
          forceSsoWhenEmailContains: this.forceSsoWhenEmailContains,
          ssoLoginUrl: this.ssoLoginUrl
        }
      });
    });
  }

  protected submitForm(): void {
    this.isLoading.set(true);
    this.warningMessages.set([]);
    this.errorMessages.set([]);
    this.authService.login(this.form.value.username, this.form.value.password).subscribe({
      next: () => {
        this.authService.goToApp(this.route.snapshot);
        this.hide();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.userService.changePasswordUserName.set(undefined);

        const result = this.loginFormService.mapLoginAuthError(error);
        this.errorMessages.set(result.errorMessages);
        this.warningMessages.set(result.warningMessages);
        this.interpolateParams.set(result.interpolateParams);
        this.loginMessagesDataTest.set(result.loginMessagesDataTest);
        this.shouldChangePassword.set(result.shouldChangePassword);

        if (result.shouldChangePassword) {
          this.userService.changePasswordUserName.set(this.form.value.username);
        }
      }
    });
  }

  protected routePasswordChange(): void {
    this.shouldChangePassword.set(false);
    this.hide();
    this.router.navigateByUrl('/login/auth/change');
  }
}
